/**
 * SSR-safe, deterministic masonry column splitter.
 *
 * Replaces CSS `columns: 2; column-fill: balance` (which recomputes an
 * unpredictable block height any time an image resolves or content changes,
 * causing the adjacent sticky sidebar ad to drift out of sync with real
 * content — see /independence overlap bug) with a pure function that
 * splits an already-known list of teasers into two CONTIGUOUS runs
 * (column one = items[0..k), column two = items[k..N)), chosen to balance
 * total estimated height as evenly as possible.
 *
 * Keeping the split contiguous (not interleaved/bin-packed) is intentional:
 * on mobile, both columns collapse via `display: contents`, and the
 * flattened DOM order (column one's items followed by column two's items)
 * must equal the original chronological order with zero extra CSS/JS.
 *
 * IMPORTANT: the height estimate below is only used to CHOOSE the split
 * point. The actual rendered height of each column is always the real,
 * deterministic height of its real DOM children (CSS `aspect-ratio` for
 * the image + normal text flow) — so an inaccurate estimate can only
 * make the two columns slightly uneven, it can NEVER reproduce the
 * original overlap bug.
 */

export type MasonryHeightInput = {
  image?: {
    crops: {
      original?: {
        srcWidth: number;
        srcHeight: number;
      };
    };
  };
  description?: string;
};

export type MasonryColumnEntry<T> = {
  item: T;
  /** Original index in the source array — preserve this for callers that
   *  need to match `configCategory.adPositioning` / `exploreMorePosition`
   *  and for stable React keys. */
  index: number;
};

export type MasonrySplitResult<T> = {
  columnOne: MasonryColumnEntry<T>[];
  columnTwo: MasonryColumnEntry<T>[];
};

export type MasonrySplitOptions = {
  /**
   * Approximate rendered width (px) of a single column at the desktop
   * breakpoint. Used only to estimate image height via aspect-ratio math;
   * does not affect actual rendered CSS width. See
   * `ConfigLatestStory.mainContentBlockColumnWidth`.
   */
  columnWidthPx: number;
  /**
   * Vertical gap (px) between stacked cards within a column, must match
   * the `.column` CSS `gap` (see
   * `--category-layout-main-content-item-original-margin-bottom`).
   * Defaults to 40 to match the CSS default.
   */
  rowGapPx?: number;
};

/**
 * Base estimated height (px) of a card's text block at desktop: breadcrumb
 * row + up to a 3-line clamped title + internal paddings/gaps. Derived from
 * the `SummaryItemSmall` CSS (-webkit-line-clamp: 3 by default) — an exact
 * value isn't derivable without real font metrics (a short headline may
 * render as 1 line), so this is a conservative constant. TUNE DURING
 * VISUAL QA against real hw/sww/nuyou headlines.
 */
export const TEXT_BLOCK_BASE_HEIGHT_PX = 110;

/**
 * Additional estimated height (px) when a 2-line clamped description is
 * present (`SummaryItemSmall`'s `afterTitle`). Only added when
 * `teaser.description` is truthy, since that IS known exactly at SSR time.
 */
export const TEXT_BLOCK_DESCRIPTION_HEIGHT_PX = 40;

/** Estimate a single card's total rendered height (px) at `columnWidthPx`. */
export function estimateCardHeightPx(
  teaser: MasonryHeightInput,
  columnWidthPx: number,
): number {
  const crop = teaser.image?.crops.original;
  const imageHeightPx =
    crop && crop.srcWidth > 0 && crop.srcHeight > 0
      ? columnWidthPx / (crop.srcWidth / crop.srcHeight)
      : columnWidthPx; // fallback: assume ~1:1 when crop is missing (placeholder image)

  const textHeightPx =
    TEXT_BLOCK_BASE_HEIGHT_PX +
    (teaser.description ? TEXT_BLOCK_DESCRIPTION_HEIGHT_PX : 0);

  return imageHeightPx + textHeightPx;
}

/**
 * Split `items` into two contiguous, height-balanced runs.
 *
 * Finds the index `k` that minimizes
 * `abs(sum(heights[0..k)) - sum(heights[k..N)))` via prefix sums — O(n).
 */
export function splitMasonryColumns<T extends MasonryHeightInput>(
  items: T[],
  { columnWidthPx, rowGapPx = 40 }: MasonrySplitOptions,
): MasonrySplitResult<T> {
  const withIndex = items.map((item, index) => ({ item, index }));

  // A lone item always goes to column one — an empty column two is the
  // correct/expected layout for a single-item page, not a tie to resolve.
  if (items.length <= 1) {
    return { columnOne: withIndex, columnTwo: [] };
  }

  const heights = items.map(
    (item) => estimateCardHeightPx(item, columnWidthPx) + rowGapPx,
  );

  const total = heights.reduce((sum, height) => sum + height, 0);

  let bestSplitIndex = 1;
  let bestDiff = Infinity;
  let leftSum = 0;
  for (let k = 1; k < items.length; k++) {
    leftSum += heights[k - 1] ?? 0;
    const diff = Math.abs(leftSum - (total - leftSum));
    if (diff < bestDiff) {
      bestDiff = diff;
      bestSplitIndex = k;
    }
  }

  return {
    columnOne: withIndex.slice(0, bestSplitIndex),
    columnTwo: withIndex.slice(bestSplitIndex),
  };
}
